/**
 * data.js — table, empty state, skeletons, stat tiles, pagination.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  - Empty states were guarded with `data && data ?` (truthy for []), so an
 *    empty list rendered a bare table with headers and no message, while the
 *    carefully written "no data" block was unreachable. Two other places used
 *    `!data && data ?`, which is always false, so the ledger and transaction
 *    tabs rendered "No Entry Found" unconditionally.
 *  - Table rows navigated on `onClick` on a `<tr>`: not focusable, no role, no
 *    keyboard handler.
 *  - `key={index}` was used in 62 places over lists that sort and filter, so
 *    deleting row 0 made React reuse row 1's input state under key 0.
 *  - Stats cards were hardcoded to the string '0' — setStats was never called.
 */

import { EmptyIcon, WarningIcon } from './icons';
import { Button, cx, Spinner } from './primitives';
import { formatMoney } from '../../lib/format';

/* ------------------------------------------------------------------ *
 * Table
 * ------------------------------------------------------------------ */

/**
 * @param {Array} columns  [{ key, header, align, width, render, srOnly }]
 * @param {Array} rows     objects with a stable `id`
 */
export function DataTable({
  columns,
  rows,
  loading = false,
  error = null,
  empty = null,
  getRowKey = (row) => row.id,
  onRetry,
  compact = false,
  caption,
  footer,
  /**
   * Lands on the scroll wrapper, which is the element a caller means.
   *
   * There was no such prop, and React drops an unknown one silently — so three
   * screens wrote `<DataTable className="mt-3" />` to space the table away from
   * an alert above it and got nothing, with nothing to tell them.
   */
  className,
}) {
  if (error) {
    return (
      <div className={cx('table-wrap', className)}>
        <ErrorState message={error} onRetry={onRetry} />
      </div>
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className={cx('table-wrap', className)}>
        <TableSkeleton columns={columns.length} />
      </div>
    );
  }

  // `rows.length === 0`, never `rows ?` — the v1 truthiness bug.
  if (!loading && rows.length === 0 && empty) {
    return <div className={cx('table-wrap', className)}>{empty}</div>;
  }

  return (
    <div className={cx('table-wrap', className)}>
      <table className={cx('table', 'table-interactive', compact && 'table-compact')}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cx(col.align === 'right' && 'col-numeric', col.shrink && 'col-shrink')}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.srOnly ? <span className="sr-only">{col.header}</span> : col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // A stable key from the document id, never the array index.
            <tr key={getRowKey(row)}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cx(col.align === 'right' && 'col-numeric', col.shrink && 'col-shrink')}
                >
                  {col.render ? col.render(row, rowIndex) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
      {loading && rows.length > 0 && (
        <div className="table-loading-bar" role="status">
          <Spinner /> Updating…
        </div>
      )}
    </div>
  );
}

/**
 * The accessible "whole row is a link" pattern: a real anchor in the first
 * cell, stretched over the row with ::after. Keyboard-reachable, screen-reader
 * announced, and middle-click/open-in-new-tab work.
 */
export function RowLink({ to, children, navigate }) {
  return (
    <a
      className="row-link"
      href={to}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/* ------------------------------------------------------------------ *
 * States
 * ------------------------------------------------------------------ */

export function EmptyState({
  icon: Icon = EmptyIcon,
  title = 'Nothing here yet',
  description,
  action,
}) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <Icon size={18} aria-hidden="true" />
      </div>
      <div>
        <div className="empty-state-title">{title}</div>
        {description && <p className="empty-state-description mt-2">{description}</p>}
      </div>
      {action && <div className="empty-state-actions">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry, title = 'Could not load this' }) {
  return (
    <div className="empty-state" role="alert">
      <div className="empty-state-icon empty-state-icon-danger">
        <WarningIcon size={18} aria-hidden="true" />
      </div>
      <div>
        <div className="empty-state-title">{title}</div>
        <p className="empty-state-description mt-2">{message}</p>
      </div>
      {onRetry && (
        <div className="empty-state-actions">
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

/* Deterministic, so the placeholder does not reshuffle on every render and
   read as flicker. */
const SKELETON_WIDTHS = [88, 62, 76, 54, 82, 68];

/**
 * A loading table keeps the real table's rhythm — a header band, hairline
 * rows, a wider first column — so nothing jumps when the data lands. The
 * visual part is hidden from assistive tech; the wrapper announces the state.
 */
export function TableSkeleton({ columns = 4, rows = 6 }) {
  return (
    <div className="table-skeleton" role="status" aria-label="Loading">
      <div aria-hidden="true">
        <div className="table-skeleton-head">
          {Array.from({ length: columns }).map((_, c) => (
            <div
              // Static placeholder list: the index is a legitimate key here.
              // eslint-disable-next-line react/no-array-index-key
              key={c}
              className="skeleton"
              style={{ flex: c === 0 ? 2 : 1, height: 7, maxWidth: c === 0 ? 120 : 72 }}
            />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={r} className="table-skeleton-row">
            {Array.from({ length: columns }).map((__, c) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={c}
                className="skeleton skeleton-text"
                style={{
                  flex: c === 0 ? 2 : 1,
                  width: `${SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length]}%`,
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Stat tile
 * ------------------------------------------------------------------ */

export function Stat({ label, value, meta, tone = 'default', icon: Icon, loading = false }) {
  return (
    <div className="stat">
      <div className="stat-label">
        {Icon && <Icon size={14} aria-hidden="true" />}
        {label}
      </div>
      <div
        className={cx(
          'stat-value',
          tone === 'positive' && 'stat-value-positive',
          tone === 'negative' && 'stat-value-negative'
        )}
      >
        {loading ? (
          /* Sized to the rendered value so the tile does not resize when the
             figure arrives. */
          <span
            className="skeleton"
            style={{ display: 'block', height: '1.2em', width: '62%', borderRadius: 'var(--radius-sm)' }}
            role="status"
            aria-label="Loading"
          />
        ) : (
          value
        )}
      </div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

export function MoneyStat({ label, paise, meta, tone, loading, icon }) {
  return (
    <Stat
      label={label}
      value={formatMoney(paise || 0)}
      meta={meta}
      tone={tone}
      loading={loading}
      icon={icon}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Pagination
 * ------------------------------------------------------------------ */

/**
 * Cursor-based "load more". v1 had no pagination of any kind — `limit()` and
 * `startAfter()` appeared zero times in the source, and every list subscribed
 * to a whole unbounded collection.
 */
export function LoadMore({ onLoadMore, loading, done, count, noun = 'records' }) {
  if (done && count === 0) return null;
  return (
    <div className="row gap-3 mt-4" style={{ justifyContent: 'center' }}>
      {done ? (
        <span className="text-xs text-tertiary">
          {count} {noun} — that is all of them
        </span>
      ) : (
        <>
          <Button variant="secondary" onClick={onLoadMore} loading={loading}>
            Load more
          </Button>
          <span className="text-xs text-tertiary">
            Showing {count} {noun}
          </span>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Money cell
 * ------------------------------------------------------------------ */

export function Money({ paise, tone, bold = false, showZero = true }) {
  if (!showZero && !paise) return <span className="text-tertiary">—</span>;
  return (
    <span
      className={cx(
        'numeric',
        bold && 'font-medium',
        tone === 'positive' && 'text-success',
        tone === 'negative' && 'text-danger'
      )}
    >
      {formatMoney(paise || 0)}
    </span>
  );
}
